// Pre-Registration Form Component
// Saves submissions to Firestore collection: pre_registrations
// Triggers notification hook on successful submission

import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { motion } from 'framer-motion';
import './PreRegistrationForm.css';

const PreRegistrationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    milkType: '',
    quantity: '',
    quantityCustom: '',
    deliveryFrequency: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const milkTypes = [
    'Buffalo',
    'Mix (Cow + Buffalo)',
    'Desi (A2)',
    'Crossbreed',
    'HF',
    'Jersey'
  ];

  const quantities = [
    '2L',
    '3L',
    '4L',
    '5L',
    'Greater than 5L'
  ];

  const deliveryFrequencies = [
    'Daily',
    'Weekly',
    'Bi-weekly'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear custom quantity if not "Greater than 5L"
      ...(name === 'quantity' && value !== 'Greater than 5L' ? { quantityCustom: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Prepare quantity value
      let finalQuantity = formData.quantity;
      if (formData.quantity === 'Greater than 5L' && formData.quantityCustom) {
        finalQuantity = `${formData.quantityCustom}L`;
      }

      // Save to Firestore collection: pre_registrations
      const docRef = await addDoc(collection(db, 'pre_registrations'), {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim().toLowerCase(),
        city: formData.city.trim(),
        milk_type: formData.milkType,
        quantity: finalQuantity,
        delivery_frequency: formData.deliveryFrequency,
        notes: formData.notes.trim() || '',
        status: 'pending',
        created_at: serverTimestamp(),
        source: 'premium_milk_landing'
      });

      console.log('Pre-registration saved with ID:', docRef.id);

      // Trigger notification hook
      // This creates a notification document that can be picked up by admin dashboard
      try {
        await addDoc(collection(db, 'notifications'), {
          type: 'pre_registration',
          title: 'New Pre-Registration',
          message: `${formData.name} from ${formData.city} registered for ${formData.milkType}`,
          data: {
            registrationId: docRef.id,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            city: formData.city,
            milkType: formData.milkType,
            quantity: finalQuantity,
            deliveryFrequency: formData.deliveryFrequency
          },
          status: 'unread',
          created_at: serverTimestamp(),
          target_roles: ['admin', 'adminassistant', 'operationhead']
        });
      } catch (notifError) {
        console.warn('Notification creation failed (non-critical):', notifError);
        // Don't fail the form submission if notification fails
      }

      // Clear form and show success
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        milkType: '',
        quantity: '',
        quantityCustom: '',
        deliveryFrequency: '',
        notes: ''
      });
      setSuccess(true);
      
      // Scroll to top of form to show success message
      setTimeout(() => {
        document.getElementById('pre-registration-form')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);

    } catch (err) {
      console.error('Error submitting pre-registration:', err);
      // Provide more specific error message
      let errorMessage = 'Failed to submit. Please try again or contact us directly.';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you have the correct permissions.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="pre-registration-form" className="pre-registration-form-container">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="pre-registration-card"
      >
        <div className="form-header">
          <h2>Join the Priority Waiting List</h2>
          <p className="form-subtitle">
            Limited quantities available. Pre-register now to secure your spot for premium, hand-milked, antibiotic-free milk.
          </p>
        </div>

        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="success-message"
          >
            <div className="success-icon">✓</div>
            <h3>You've been added to the waiting list!</h3>
            <p>We'll contact you as soon as your preferred milk type becomes available.</p>
          </motion.div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="pre-registration-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="10-digit mobile number"
                  pattern="[0-9]{10}"
                  maxLength="10"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">City / Area *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="Your city or delivery area"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="milkType">Milk Type *</label>
                <select
                  id="milkType"
                  name="milkType"
                  value={formData.milkType}
                  onChange={handleChange}
                  required
                  className="gold-input"
                >
                  <option value="">Select milk type</option>
                  {milkTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="deliveryFrequency">Delivery Frequency *</label>
                <select
                  id="deliveryFrequency"
                  name="deliveryFrequency"
                  value={formData.deliveryFrequency}
                  onChange={handleChange}
                  required
                  className="gold-input"
                >
                  <option value="">Select frequency</option>
                  {deliveryFrequencies.map(freq => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quantity">Quantity per Delivery *</label>
                <select
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  className="gold-input"
                >
                  <option value="">Select quantity</option>
                  {quantities.map(qty => (
                    <option key={qty} value={qty}>{qty}</option>
                  ))}
                </select>
              </div>

              {formData.quantity === 'Greater than 5L' && (
                <div className="form-group">
                  <label htmlFor="quantityCustom">Specify Quantity (Liters) *</label>
                  <input
                    type="number"
                    id="quantityCustom"
                    name="quantityCustom"
                    value={formData.quantityCustom}
                    onChange={handleChange}
                    required
                    min="6"
                    className="gold-input"
                    placeholder="e.g., 6, 7, 10..."
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="notes">Additional Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="gold-input"
                placeholder="Any special requirements or preferences..."
                rows="3"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-submit"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Pre-Register Now'}
            </button>

            <p className="form-footer">
              By submitting, you agree to be contacted when your preferred milk type becomes available.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default PreRegistrationForm;

